import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ITask } from '@fi-shared/fi-task-shared/fi-task-shared';
import { AppTextService } from '@fi-shared/app-text';
import { ICommentModel, ICommentsContainer } from '@fi-shared/fi-comments/fi-comments';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { IAccessRightFilterCollection, ILightWeightUser } from '@fi-shared/models';
import { FiTaskHelperService } from '@fi-shared/fi-task-shared/fi-task-helper.service';
import { FiTaskSharedService } from '@fi-shared/fi-task-shared/fi-task-shared.service';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { Observable, Subject } from 'rxjs';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { RollupContentComponent } from '@fi-shared/rollup/rollup-content.component';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import {
    FiDropdownActionGuard,
    FiDropdownActionResult,
    FiDropdownItemDisabledFn,
} from '@fi-shared/fi-select-search/fi-select-search';
import { FiChipsSelectSearchComponent } from '@fi-shared/fi-select-search/fi-chips-select-search/fi-chips-select-search.component';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiCommentsListStateService } from '@fi-shared/fi-comments/fi-comments-list/services/fi-comments-list-state.service';
import { FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';

@Component({
    selector: 'fi-task-details',
    templateUrl: './task-details.component.html',
    styleUrls: ['./task-details.component.scss', '../task-list/task-list-item/task-item-shared.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-details' },
    standalone: false,
})
export class TaskDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() task: ITask;
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() comments: ICommentModel[];
    @Input() scrollableElement: HTMLElement;

    ObjectTypes = ObjectTypes;
    ProfileSizes = FiProfileSizes;
    availableUsers: ILightWeightUser[];

    self = this;

    @ViewChild(FiChipsSelectSearchComponent, { static: false })
    usersSelectorComponent: FiChipsSelectSearchComponent<ILightWeightUser>;
    @ViewChild(RollupContentComponent, { static: true }) commentsRollupContent: RollupContentComponent;

    get assignedUserIds() {
        return this.task.AccessRightFilterCollection.AccessRightUserFilter.UserIds;
    }
    set assignedUserIds(value) {
        this.task.AccessRightFilterCollection.AccessRightUserFilter.UserIds = value;
    }

    constructor(
        private appText: AppTextService,
        private cdr: ChangeDetectorRef,
        private taskSharedService: FiTaskSharedService,
        private taskHelperService: FiTaskHelperService,
        private userDataService: FiUserDataService,
        private fiDialogsService: FiDialogsService,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private sharedActionsBaseService: SharedActionsBaseService,
        private fiFormatDateService: FiFormatDateService,
        private commentsListStateService: FiCommentsListStateService,
    ) {}

    ngOnInit() {
        this.availableUsers = this.accessRightFilterCollection.AccessRightUserFilter.Users;
        this.setAssignedUserIdsForPrivateTask();
    }

    protected get hasDeleteAccessOfOwnerItem(): boolean {
        return this.taskHelperService.hasEditAccess(this.task);
    }

    private setAssignedUserIdsForPrivateTask() {
        if (this.isUsersAssignee() && !this.assignedUserIds.length) {
            this.assignedUserIds = [this.task.CreatedByUserId];
        }
    }

    get commentsCount(): number {
        return this.commentsListStateService.getCommentsCount(ObjectTypes.Task, this.task.Id);
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    protected get isAutomatedTask(): boolean {
        return this.taskHelperService.isAutomatedTask(this.task.TaskType);
    }

    getCommentsHeader() {
        return this.appText.get('common.comments') + ' (' + this.commentsCount + ')';
    }

    getTaskClass() {
        return this.taskHelperService.getTaskClass(this.task);
    }
    getDeadlineText() {
        return this.taskHelperService.getDeadlineText(this.task);
    }
    getCommentsText() {
        return this.taskHelperService.getCommentsText(this.task);
    }

    getCreatedOnText(): string {
        return this.appText
            .get('task.posted')
            .replace('{0}', this.fiFormatDateService.formatDate(this.task.CreatedOn, 'date'));
    }

    onCompletedClick() {
        this.taskSharedService.saveTaskCompleted(
            [this.task],
            this.task.IsCompleted,
            () => {
                this.cdr.markForCheck();
                this.taskHelperService.taskCompletedChanged$.next({
                    taskId: this.task.Id,
                    isCompleted: this.task.IsCompleted,
                });
            },
            () => {
                this.task.IsCompleted = !this.task.IsCompleted;
            },
            true,
        );
    }

    showRequestAssistance(): boolean {
        //return this.task.CreatedByUserId !== this.userDataService.getUserId();
        return true;
    }

    isUsersAssignee(): boolean {
        return (
            !this.task.AccessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds.length &&
            !this.task.AccessRightFilterCollection.AccessRightCoreFilter.RoleIds.length
        );
    }

    onRequestAssistanceClick($event: MouseEvent) {
        $event.preventDefault();
        $event.stopPropagation();
        this.usersSelectorComponent.openPanel();
        //this.objectsSelectorComponent.openDropdown(true);
        /*setTimeout(() => {
            if (this.scrollableElement) {
                this.scrollToElementAnimateService.scrollToY(this.scrollableElement, 300);
            }
        });*/
    }

    isRemoveAssignedUserDisabled: FiDropdownItemDisabledFn<ILightWeightUser> = (user) => {
        // user can't delete assignees created by other users
        const currentUserId = this.userDataService.getUserId();
        if (this.task.CreatedByUserId === currentUserId) return true;

        const accessRightChangedByItem = this.task.AccessRightFilterCollection.CreatedByData.find(
            (item) => item.ObjectType === ObjectTypes.User && item.ObjectId === user.Id,
        );
        return (
            accessRightChangedByItem && accessRightChangedByItem.CreatedByUserId !== this.userDataService.getUserId()
        );
    };

    onAddingAssignedUser: FiDropdownActionGuard<ILightWeightUser> = ($event) => {
        return new Promise<FiDropdownActionResult>((resolve, reject) => {
            this.taskSharedService
                .changeTaskAssignees(this.task.Id, [$event.item.Id], ActionType.Create)
                .then(() => resolve({ result: true }), reject);
        });
    };

    onDeletingAssignedUser: FiDropdownActionGuard<ILightWeightUser> = ($event) => {
        return new Promise<FiDropdownActionResult>((resolve, reject) => {
            this.fiDialogsService
                .confirm({
                    headerText: this.appText.get('task.dialog_remove_assignee_header'),
                    messageText: this.appText.get('task.dialog_remove_assignee_message', {
                        '{0}': $event.item.FullName,
                    }),
                    yesButtonText: this.appText.get('common.remove'),
                })
                .onYesButtonClick(() => {
                    this.taskSharedService
                        .changeTaskAssignees(this.task.Id, [$event.item.Id], ActionType.Delete)
                        .then(() => resolve({ result: true }), reject);
                })
                .onNoButtonClick(() => resolve({ result: false }));
        });
    };

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    toggleCommentsContent() {
        this.commentsRollupContent.toggleContent();
        if (this.commentsRollupContent.opened) {
            setTimeout(() => {
                this.scrollToElementAnimateService.scrollToY(
                    this.scrollableElement,
                    this.scrollableElement.scrollHeight,
                );
                this.commentsListStateService.emitCommentUpdateConnectionLine(ObjectTypes.Task, this.task.Id);
            }, 100);
        }
    }

    onSelectedUserClick($event: ILightWeightUser) {
        this.sharedActionsBaseService.openUserViewPopup($event.Id);
    }
}
